import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { apiService } from '../services/api';
import { MonitoringActivationLogList } from './MonitoringActivationLogList';
import { ChangeEmailDeliveryLogList } from './ChangeEmailDeliveryLogList';

interface AgencyMonitoringDetailLogsPanelProps {
  agencyCode: string | null;
  agencyName: string | null;
  enabled?: boolean;
}

export function AgencyMonitoringDetailLogsPanel({
  agencyCode,
  agencyName,
  enabled = true,
}: AgencyMonitoringDetailLogsPanelProps) {
  const [activeTab, setActiveTab] = useState(0);

  const { data: historyData, isLoading: historyLoading } = useQuery(
    ['agency-history-detail', agencyCode],
    () => apiService.getAgencyMonitoringHistory(agencyCode!),
    { enabled: enabled && !!agencyCode, refetchOnWindowFocus: false },
  );

  const { data: emailLogs, isLoading: emailLoading } = useQuery(
    ['agency-delivery-logs', agencyCode],
    () => apiService.getAgencyDeliveryLogs(agencyCode!),
    { enabled: enabled && !!agencyCode, refetchOnWindowFocus: false },
  );

  const history = historyData?.history ?? [];
  const emailCount = emailLogs?.length ?? 0;
  const tabLabels: Array<{ key: 'activation' | 'email'; label: string }> = [
    { key: 'activation', label: `Monitoramento (${history.length})` },
    { key: 'email', label: `E-mails (${emailCount})` },
  ];
  const currentKey = tabLabels[activeTab]?.key ?? 'activation';

  if (!agencyCode) return null;

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="Abas de logs da agência"
        >
          {tabLabels.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {currentKey === 'activation' && (
        <MonitoringActivationLogList
          history={history}
          loading={historyLoading}
          isActive={historyData?.is_monitored}
          activeEntityLabel={agencyName ?? historyData?.agency_name}
          activeStatusMessage={
            historyData?.is_monitored && (agencyName ?? historyData?.agency_name)
              ? `Agência «${agencyName ?? historyData?.agency_name}» está ativa no momento.`
              : null
          }
        />
      )}

      {currentKey === 'email' && (
        <ChangeEmailDeliveryLogList logs={emailLogs ?? []} loading={emailLoading} />
      )}
    </Box>
  );
}
